import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { labAPI, workerAPI } from '../../services/api';
import { useTranslation } from 'react-i18next';

const LabWorkerDashboard = () => {
  const [stats, setStats] = useState({
    workers: {
      total: 0,
      active: 0,
      inactive: 0,
    },
    labTasks: {
      total: 0,
      pending: 0,
      assigned: 0,
      inProgress: 0,
      completed: 0,
      onHold: 0,
      cancelled: 0,
    },
  });
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [tasksByStatus, setTasksByStatus] = useState({
    Pending: [],
    Assigned: [],
    'In Progress': [],
    Completed: [],
    'On Hold': [],
    'Cancelled': [],
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      const [workersResponse, labResponse] = await Promise.all([
        workerAPI.getAll({ limit: 1000 }),
        labAPI.getAll({ limit: 1000 })
      ]);

      const workers = workersResponse.data.data;
      const labTasks = labResponse.data.data;

      const workerStats = {
        total: workers.length,
        active: workers.filter(w => w.is_active).length,
        inactive: workers.filter(w => !w.is_active).length,
      };

      const taskStats = {
        total: labTasks.length,
        pending: labTasks.filter(t => t.status === 'Pending').length,
        assigned: labTasks.filter(t => t.status === 'Assigned').length,
        inProgress: labTasks.filter(t => t.status === 'In Progress').length,
        completed: labTasks.filter(t => t.status === 'Completed').length,
        onHold: labTasks.filter(t => t.status === 'On Hold').length,
        cancelled: labTasks.filter(t => t.status === 'Cancelled').length,
      };

      setStats({
        workers: workerStats,
        labTasks: taskStats,
      });

      const grouped = {
        Pending: labTasks.filter(t => t.status === 'Pending'),
        Assigned: labTasks.filter(t => t.status === 'Assigned'),
        'In Progress': labTasks.filter(t => t.status === 'In Progress'),
        Completed: labTasks.filter(t => t.status === 'Completed'),
        'On Hold': labTasks.filter(t => t.status === 'On Hold'),
        'Cancelled': labTasks.filter(t => t.status === 'Cancelled'),
      };

      setTasksByStatus(grouped);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const completionRate = stats.labTasks.total > 0
    ? Math.round((stats.labTasks.completed / stats.labTasks.total) * 100)
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-300 border-t-yellow-500 mx-auto mb-4"></div>
          <p className="text-gray-700 font-medium">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header Section - Gray Background */}
      <div className="bg-gray-500 px-6 py-8 mb-8">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-4xl font-bold text-gray-900">{t('lab.dashboard')}</h1>
          <Link
            to="/lab/create"
            className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold px-6 py-3 rounded-lg transition-colors shadow-lg hover:shadow-xl"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Task
          </Link>
        </div>
        <p className="text-gray-600 text-lg">{t('lab.dashboardSubtitle')}</p>
      </div>

      {/* Content Section */}
      <div className="p-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Total Workers */}
        <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium mb-2">Total Workers</p>
              <p className="text-3xl font-bold text-gray-900">{stats.workers.total}</p>
              <p className="text-xs text-gray-500 mt-3">
                <span className="text-gray-700 font-semibold">{stats.workers.active}</span> active
              </p>
            </div>
            <div className="bg-yellow-100 p-3 rounded-lg">
              <svg className="w-6 h-6 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v4h8v-4zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Total Tasks */}
        <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium mb-2">Total Tasks</p>
              <p className="text-3xl font-bold text-gray-900">{stats.labTasks.total}</p>
              <p className="text-xs text-gray-500 mt-3">
                <span className="text-gray-700 font-semibold">{stats.labTasks.inProgress}</span> in progress
              </p>
            </div>
            <div className="bg-yellow-100 p-3 rounded-lg">
              <svg className="w-6 h-6 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M5 3a2 2 0 00-2 2v6h6V5a2 2 0 00-2-2H5zM15 3a2 2 0 00-2 2v6h6V5a2 2 0 00-2-2h-2zM5 13a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM15 13a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2h-2z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Completion Rate */}
        <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="text-gray-600 text-sm font-medium mb-2">Completion Rate</p>
              <p className="text-3xl font-bold text-gray-900">{completionRate}%</p>
              <div className="mt-3 w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-yellow-500 h-2 rounded-full transition-all duration-300"
                  style={{width: `${completionRate}%`}}
                ></div>
              </div>
            </div>
            <div className="bg-yellow-100 p-3 rounded-lg">
              <svg className="w-6 h-6 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
        </div>

        {/* Pending Tasks */}
        <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium mb-2">Pending</p>
              <p className="text-3xl font-bold text-gray-900">{stats.labTasks.pending + stats.labTasks.assigned}</p>
              <p className="text-xs text-gray-500 mt-3">Tasks awaiting assignment</p>
            </div>
            <div className="bg-yellow-100 p-3 rounded-lg">
              <svg className="w-6 h-6 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 2a1 1 0 011-1h6a1 1 0 011 1v12a1 1 0 11-2 0V3H5a1 1 0 01-1-1zm9 4a1 1 0 00-1 1v8a1 1 0 001 1h2a1 1 0 001-1V7a1 1 0 00-1-1h-2zm4 4a1 1 0 00-1 1v4a1 1 0 001 1h2a1 1 0 001-1v-4a1 1 0 00-1-1h-2z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Task Board</h2>
          <p className="text-gray-600 text-sm mt-1">Manage tasks by status</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {['Pending', 'Assigned', 'In Progress', 'Completed', 'On Hold', 'Cancelled'].map(status => (
            <div key={status} className="bg-gray-50 rounded-lg p-4">
              {/* Column Header */}
              <div className="flex items-center gap-3 mb-4 pb-4 border-b-2 border-yellow-400">
                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <h3 className="font-bold text-gray-900 text-lg">{status}</h3>
                <span className="ml-auto bg-gray-900 text-white text-xs font-bold px-3 py-1 rounded-full">
                  {tasksByStatus[status]?.length || 0}
                </span>
              </div>

              {/* Task Cards */}
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {tasksByStatus[status]?.length > 0 ? (
                  tasksByStatus[status].map(task => (
                    <Link
                      key={task.id}
                      to={`/lab/${task.id}`}
                      className="block bg-white rounded-lg p-4 hover:bg-yellow-50 transition-all hover:shadow-md active:scale-95 cursor-pointer"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <span className="font-bold text-gray-900">{task.lab_number}</span>
                        <svg className="w-5 h-5 text-yellow-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                      <div className="space-y-2">
                        <p className="text-xs text-gray-600">{new Date(task.created_at).toLocaleDateString()}</p>
                        <p className="text-xs font-medium text-gray-700">SO-{task.sales_order_id}</p>
                      </div>
                    </Link>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    <svg className="w-10 h-10 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                    </svg>
                    <p className="text-sm">No tasks</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      </div>
    </div>
  );
};

export default LabWorkerDashboard;
